import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, Field, FormGrid, ModalError } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { apiPost, ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';

/**
 * NOWY BĘBEN — KREATOR ODWZOROWANY Z SPSUI, ale z podsumowaniem, którego SPSUI nie ma.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ TO NAJDALEJ IDĄCY ZAPIS, JAKI PORTAL WYKONUJE: zakłada KONSTRUKCJĘ maszyny. Zła geometria
 *  nie objawia się błędem — objawia się automatem, który wskazuje inną fizyczną komórkę, niż
 *  operator kliknął. Dlatego przed przyciskiem stoi liczba wierszy, które powstaną, a nie samo
 *  „Utwórz": kto zobaczy „54 szuflady, 702 przegrody", ten zauważy pomyłkę w rzędzie wielkości.
 *
 *  ⚠️ KOLEJNOŚĆ KROKÓW JEST TAKA JAK W SPSUI (najpierw typ ramy, potem geometria) CELOWO —
 *  serwis stawia je obok siebie przy maszynie i porównuje. Nasza „lepsza" kolejność zmuszałaby
 *  do tłumaczenia jednego na drugie.
 *
 *  ⚠️ DYNABOX I LOCKERBOX SĄ WIDOCZNE, ALE WYŁĄCZONE, i to jest treść, nie brak funkcji.
 *  Ich wzorca wierszy nie znamy: jedyny DynaBox, jakiemu możemy się przyjrzeć, BYŁ po utworzeniu
 *  systemowo poszerzany (611 przegród przy 13 × 54 = 702), więc odtwarzanie „świeżego" bębna
 *  z niego byłoby zgadywaniem. Ukrycie ich znaczyłoby „portal tego nie umie i nie wiadomo
 *  dlaczego"; wyłączenie z powodem mówi, czego brakuje.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Liczby kolumn z rozwijanej listy SPSUI — nie z naszego pomysłu. Serwer sprawdza to samo. */
const KOLUMNY = [9, 18, 36, 54] as const;

type Typ = 'ROTOPOINT' | 'DYNABOX' | 'LOCKERBOX';

const TYPY: readonly { id: Typ; nazwa: string; modeType: number; dostepny: boolean }[] = [
  { id: 'ROTOPOINT', nazwa: 'Rotopoint', modeType: 9, dostepny: true },
  { id: 'DYNABOX', nazwa: 'DynaBox', modeType: 25, dostepny: false },
  { id: 'LOCKERBOX', nazwa: 'LockerBox', modeType: 24, dostepny: false },
];

export function NowyBebenDialog({
  maszyna,
  groupId,
  onClose,
}: {
  maszyna: { id: string; name: string };
  groupId: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const klient = useQueryClient();

  const [typ, setTyp] = useState<Typ | null>(null);
  /** Domyślne 13 — tyle ma prawdziwy Rotopoint na testówce, i tyle było wybrane w oknie SPSUI. */
  const [poziomy, setPoziomy] = useState(13);
  const [kolumny, setKolumny] = useState<number>(36);
  const [zapisuje, setZapisuje] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);

  const utworz = async (): Promise<void> => {
    setZapisuje(true);
    setBlad(null);
    try {
      const w = await apiPost<{ opId: string; picNumber: number; szuflad: number; przegrod: number }>(
        `/machines/${maszyna.id}/moduly/rotopoint`,
        { groupId, poziomy, kolumny },
      );
      toast.success(
        t('beben.zlecono')
          .replace('{pic}', String(w.picNumber))
          .replace('{szuflad}', String(w.szuflad))
          .replace('{przegrod}', String(w.przegrod)),
      );
      await klient.invalidateQueries({ queryKey: ['machines', 'moduly', maszyna.id, groupId] });
      onClose();
    } catch (e) {
      setBlad(e instanceof ApiError ? e.message : t('common.error'));
    } finally {
      setZapisuje(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={`${t('beben.tytul')} — ${maszyna.name}`}>
        <FormGrid>
          {/*
            ⚠️ OSTRZEŻENIE PADA PRZED KLIKNIĘCIEM, NIE PO. Ta operacja zmienia konstrukcję maszyny
            i nie ma przycisku „cofnij" — bęben trzeba by usuwać ręcznie w SPSUI.
          */}
          <div className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3 text-xs">
            <p className="flex items-center gap-2 font-medium">
              <AlertTriangle className="size-4" />
              {t('beben.ostrzezenieTytul')}
            </p>
            <p className="mt-1">{t('beben.ostrzezenieTresc')}</p>
          </div>

          {/* KROK 1 — typ ramy, dokładnie jak `Specify Frame Type` w SPSUI. */}
          <Field label={t('beben.typRamy')} htmlFor="beben-typ">
            <div className="flex flex-col gap-1">
              {TYPY.map((x) => (
                <label
                  key={x.id}
                  className={`flex items-center gap-2 text-sm ${x.dostepny ? '' : 'text-muted-foreground'}`}
                >
                  <input
                    type="radio"
                    name="beben-typ"
                    disabled={!x.dostepny}
                    checked={typ === x.id}
                    onChange={() => setTyp(x.id)}
                  />
                  <span>
                    {x.nazwa}
                    <span className="ml-1 font-mono text-xs opacity-70">MODETYPE {x.modeType}</span>
                  </span>
                  {!x.dostepny && <span className="text-xs">— {t('beben.niedostepny')}</span>}
                </label>
              ))}
            </div>
          </Field>

          {/* KROK 2 — geometria; pokazuje się dopiero po wyborze typu, jak w SPSUI. */}
          {typ === 'ROTOPOINT' && (
            <>
              <Field label={t('beben.poziomy')} htmlFor="beben-poziomy">
                <input
                  id="beben-poziomy"
                  type="number"
                  min={1}
                  max={40}
                  value={poziomy}
                  onChange={(e) => setPoziomy(Number(e.target.value))}
                  className="h-9 w-full rounded-md border bg-transparent px-3 text-sm"
                />
              </Field>

              <Field label={t('beben.kolumny')} htmlFor="beben-kolumny">
                <select
                  id="beben-kolumny"
                  value={kolumny}
                  onChange={(e) => setKolumny(Number(e.target.value))}
                  className="h-9 w-full rounded-md border bg-transparent px-3 text-sm"
                >
                  {KOLUMNY.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </Field>

              {/*
                ⚠️ PODSUMOWANIE JEST TU PO TO, ŻEBY DAŁO SIĘ ZAUWAŻYĆ POMYŁKĘ. „54 kolumny × 13
                poziomów" nic nie mówi o skali; „702 przegrody" mówi od razu, czy to ta maszyna.
              */}
              <p className="rounded-md border bg-muted/40 p-3 text-xs">
                {t('beben.podsumowanie')
                  .replace('{szuflad}', String(kolumny))
                  .replace('{przegrod}', String(kolumny * poziomy))}
              </p>
            </>
          )}

          {blad !== null && <ModalError>{blad}</ModalError>}
        </FormGrid>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={utworz} disabled={typ !== 'ROTOPOINT' || zapisuje}>
            {zapisuje && <Loader2 className="size-4 animate-spin" />}
            {t('beben.utworz')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
